"use Client";
import React, { useCallback, useEffect, useState } from "react";
import Icon from "../icons/Icons";
import CustomModal from "../modal/CustomModal";
import { AnimatePresence, motion } from "framer-motion";
import Lottie from "lottie-react";
import successAnimation from "./../../../public/next_assets/lottie/success-tick.json";
import lockLottie from "./../../../public/next_assets/lottie/lock-lottie.json";
import { apiPost } from "@/utils/apiClient";
import { validateName, validatePhone } from "@/utils/validation";
import { usePathname, useRouter } from "next/navigation";
import { addToast } from "../ui/CustomToastManager";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  closeRequestCallback,
  markRequestCallbackSuccess,
} from "@/redux/features/requestCallback/requestCallbackSlice";
import Image from "next/image";
import {
  isRequestCallbackDone,
  setRequestCallbackDone,
} from "@/utils/cookieUtils";
import { ROOT_URL } from "@/utils/gloableVariables";

interface ErrorMap {
  name?: string;
  phone?: string;
}

export default function RequestCallbackModal({
  pageKey,
}: {
  pageKey?: string;
}) {
  const [userName, setUserName] = useState("");
  const [userPhoneNo, setUserPhoneNo] = useState("");
  const [errors, setErrors] = useState<ErrorMap>({});
  const [token, setToken] = useState("");
  const [msg, setMsg] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const pathname = usePathname();
  const shouldLoadScripts = process.env.NODE_ENV != "development";
  const router = useRouter();
  const pdfPath = `${ROOT_URL}financial-planning/retirement-planning-roadmap.pdf`;

  const dispatch = useDispatch();
  const { isOpen, reason, buttonType, completed } = useSelector(
    (state: RootState) => state.requestCallbackModal,
  );
  const completeCookie = isRequestCallbackDone();

  /* ----------------------------- INPUT HANDLERS ----------------------------- */
  const handleNameInput = (raw: string) => {
    const value = raw
      .replace(/\s{2,}/g, " ")
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");

    setUserName(value);

    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleNoInput = (raw: string) => {
    const value = raw.replace(/\D/g, "").slice(0, 10);

    setUserPhoneNo(value);

    if (errors.phone) {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  /* ----------------------------- BLUR VALIDATIONS ----------------------------- */
  const handleNameBlur = () => {
    if (!userName.trim()) return;

    if (!validateName(userName)) {
      setErrors((prev) => ({ ...prev, name: "Enter A Valid Name" }));
    }
  };

  const handlePhoneBlur = () => {
    if (!userPhoneNo.trim()) return;

    if (!validatePhone(userPhoneNo)) {
      setErrors((prev) => ({ ...prev, phone: "Enter Valid Phone Number" }));
    }
  };

  const generateToken = useCallback(async () => {
    const okName = validateName(userName);
    const okPhone = validatePhone(userPhoneNo);

    if (!okName || !okPhone || !pathname) {
      setToken("");
      return;
    }

    try {
      const res = await fetch("/api/encryptJsonData", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          value: { name: userName, mobile: userPhoneNo, source: pathname },
        }),
      });

      const data = await res.json();
      setToken(data.encrypted || "");
    } catch {
      setToken("");
    }
  }, [userName, userPhoneNo, pathname]);

  useEffect(() => {
    generateToken();
  }, [generateToken]);

  /* ----------------------------- JWT Token ----------------------------- */
  useEffect(() => {
    if (!shouldLoadScripts) return;
    async function fetchToken() {
      try {
        const res = await fetch("/api/getToken");
        if (!res.ok) throw new Error("Failed to fetch internal token");
        const data = await res.json();
        setJwtToken(data.token);
      } catch (err) {
        console.error("Error fetching internal token:", err);
      }
    }

    fetchToken();

    const interval = setInterval(fetchToken, 4 * 60 * 1000);

    return () => clearInterval(interval);
  }, [shouldLoadScripts]);

  /* ----------------------------- RESET FIELDS ----------------------------- */
  const resetFields = () => {
    setToken("");
    setUserName("");
    setUserPhoneNo("");
    setSuccess(false);
    setErrors({});
  };

  const handleClose = () => {
    resetFields();
    dispatch(closeRequestCallback());
  };

  /* ----------------------------- SUBMIT HANDLER ----------------------------- */
  const handleContinue = async () => {
    const okName = validateName(userName);
    const okPhone = validatePhone(userPhoneNo);

    const newErrors: ErrorMap = {};
    if (!okName) newErrors.name = "Enter a Valid Name";
    if (!okPhone) newErrors.phone = "Enter Valid Phone Number";

    if (!okName || !okPhone) {
      setErrors(newErrors);
      return;
    }

    if (!token) {
      addToast({
        title: "Error",
        description: "Something went wrong. Try again.",
        status: "failed",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // const payload = { data_token: token };
      const payload = {
        data_token: {
          name: userName,
          mobile: userPhoneNo,
          source: pathname,
          reason: reason || "Callback Request",
        },
      };

      const res = await apiPost("requestCallback", payload, {
        base: "base2",
        useAuth: false,
        headers: {
          "X-internal-token": jwtToken || "",
        },
      });

      if (!res?.status) {
        addToast({
          title: "Request Call",
          description: res?.message || "Failed to Request Call",
          status: "failed",
        });

        setIsSubmitting(false);
        return;
      }

      dispatch(markRequestCallbackSuccess("ok"));
      setRequestCallbackDone();
      setMsg(res.message);
      setSuccess(true);

      setTimeout(() => {
        if (buttonType === "retirement-roadmap-strip") {
          router.push(pdfPath);
        }

        setTimeout(() => {
          handleClose();
        }, 300);
      }, 1500);
    } catch (error) {
      console.error("Something went wrong!", error);
      addToast({
        title: "",
        description: "Something went wrong",
        status: "failed",
      });
      setIsSubmitting(false);
    }
  };

  /* ----------------------------- Checking if Already Requested ----------------------------- */
  useEffect(() => {
    if (!isOpen) return;

    if (completed["ok"] || completeCookie) {
      setSuccess(true);
    }
  }, [completed["ok"], isOpen]);

  /* ----------------------------- Changing the strip and mode according to the button and page type ----------------------------- */
  let dark = false;
  let stripKey = <DefaultStrip />;

  if (pageKey === "digital-gold" || pageKey === "sif") {
    dark = true;
  }
  if (buttonType === "overlap_strip") {
    stripKey = <OverlapPageStrip />;
  }
  if (buttonType === "gold_strip" || pageKey === "digital-gold") {
    stripKey = <GoldPageStrip />;
  }
  if (buttonType === "retirement-roadmap-strip") {
    stripKey = <RetirementRoadmapStrip />;
  }

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleClose}
        maxSize="max-w-[800px]"
        isBlurOverlay
        className={`border-x border-t sm:border rounded-t-6xl sm:rounded-7xl sm:p-7 flex flex-col sm:flex-row items-stretch sm:gap-7 border-white ${dark ? "bg-black!" : "border-black bg-[#F0F6FF]!"}`}
      >
        {/* Strip */}
        {stripKey}

        <div className="h-72 sm:h-62 min-w-80.75">
          <AnimatePresence mode="wait">
            {!success && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col w-full p-5 sm:p-0 h-full"
              >
                <div
                  className={`font-medium text-sm leading-1.1 uppercase mb-2 ${dark ? "text-white" : "text-black"}`}
                >
                  FULL NAME
                </div>

                <label
                  className={`relative rounded-xl p-2.5 border mb-6 transition duration-300 ${dark ? "bg-[#141414] text-white/65 focus-within:text-white" : "bg-white text-primaryBlack/65 focus-within:text-primaryBlack"} ${errors.name ? "border-[#ff3535]" : dark ? "border-[#3A3A3A] focus-within:border-white hover:border-white" : "border-[#d3d3d3] focus-within:border-primaryBlack hover:border-primaryBlack"}`}
                >
                  <input
                    type="text"
                    value={userName}
                    onBlur={handleNameBlur}
                    onChange={(e) => handleNameInput(e.target.value)}
                    className="border-none outline-hidden bg-transparent leading-1 px-1"
                    placeholder="Enter your Name here"
                  />

                  {errors.name && (
                    <p className="absolute -bottom-5.5 left-3 text-xs text-[#ff3535] max-w-[90%] line-clamp-1">
                      {errors.name}
                    </p>
                  )}
                </label>

                <div
                  className={`font-medium text-sm leading-1.1 uppercase mb-2 ${dark ? "text-white" : "text-black"}`}
                >
                  Your Mobile Number
                </div>

                <label
                  className={`rounded-xl border mb-5 transition duration-300 flex items-center gap-2.5 relative ${dark ? "bg-[#141414] text-white/65 focus-within:text-white" : "bg-white text-primaryBlack/65 focus-within:text-primaryBlack"} ${errors.phone ? "border-[#ff3535]" : dark ? "border-[#3A3A3A] focus-within:border-white hover:border-white" : "border-[#d3d3d3] focus-within:border-primaryBlack hover:border-primaryBlack"}`}
                >
                  <span
                    className={`inline-block p-2 rounded-l-[11px] ${dark ? "bg-white/10" : "bg-black/10"} }`}
                  >
                    <Icon.indianflagIcon />
                  </span>

                  <span
                    className={`absolute h-fit inset-y-0 left-14 my-auto leading-1 ${dark ? "text-white/60" : "text-black/60"}`}
                  >
                    +91 -
                  </span>
                  <input
                    type="tel"
                    value={userPhoneNo}
                    onBlur={handlePhoneBlur}
                    onChange={(e) => handleNoInput(e.target.value)}
                    className="pl-11 border-none outline-hidden bg-transparent leading-1"
                    placeholder="9999999999"
                  />

                  {errors.phone && (
                    <p className="absolute -bottom-5.5 left-3 text-xs text-[#ff3535] max-w-[90%] line-clamp-1">
                      {errors.phone}
                    </p>
                  )}
                </label>

                <button
                  onClick={handleContinue}
                  disabled={isSubmitting}
                  aria-label="Continue to Request a Call"
                  className={`rounded-full border w-full font-medium leading-1 py-3.5 text-center mt-auto transition duration-300 hover:bg-transparent ${dark ? "bg-white border-white text-primaryBlack hover:text-white" : "bg-primaryBlack border-black text-white/80 hover:text-black"}`}
                >
                  {isSubmitting ? "Processing..." : "Continue"}
                </button>
              </motion.div>
            )}

            {success && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
                className="w-full h-full flex items-center justify-center"
              >
                <div className="relative">
                  <Lottie
                    className="w-32 h-32 mx-auto"
                    animationData={successAnimation}
                    loop={true}
                    autoplay
                  />
                  <p
                    className={`text-lg sm:text-xl font-semibold mb-2 text-center mt-2.5 ${dark ? "text-white/80" : "text-black/80"}`}
                  >
                    {msg || "Callback already requested"}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </CustomModal>
    </>
  );
}

function GoldPageStrip() {
  return (
    <div className="w-full sm:max-w-md border-b sm:border-2 border-white/50 bg-[linear-gradient(107.32deg,rgba(220,148,13,0.25)_38.01%,rgba(118,79,7,0.25)_97.14%)] p-5 sm:p-6 text-white relative overflow-hidden rounded-t-6xl sm:rounded-4xl">
      <i className="absolute top-10 left-20 w-14 z-0 shadow-[-20px_-20px_120px_90px_#c1a021a3]" />
      <Icon.callIconV3 className="w-9 h-9 sm:w-10 sm:h-10 mb-6 sm:mb-9 relative z-1" />
      <div className="text-2xl mb-3 sm:mb-4">
        Get a Free Callback from Our Experts
      </div>
      <div className="opacity-65 text-2xs sm:text-sm sm:leading-5 leading-1.1">
        Our team will call you and guide you through every mutual fund query and
        concern.
      </div>
    </div>
  );
}

function OverlapPageStrip() {
  return (
    <div className="w-full sm:max-w-md border-b sm:border-2 border-white/50 bg-[linear-gradient(107.32deg,#1A51AA_38.01%,#092758_97.14%)] p-5 sm:p-6 text-white relative overflow-hidden rounded-t-6xl sm:rounded-4xl">
      <Lottie
        animationData={lockLottie}
        loop={true}
        autoplay={true}
        className="w-16 object-contain mb-6 sm:mb-3 relative"
      />
      <Image
        src="/next_assets/images/overlap/sun-rays-circle.png"
        alt="Highlighted Icon Background Image"
        width={250}
        height={250}
        className="absolute top-0 left-0 opacity-45 translate-x-[-27%] translate-y-[-27%] pointer-events-none"
      />

      <div className="text-2xl mb-3 sm:mb-4">
        Unlock your free 15-Min MF Portfolio Review
      </div>
      <div className="opacity-65 text-2xs sm:text-sm sm:leading-5 leading-1.1">
        Our Experts will call you and fix Overlaps and Boost Returns with you.
      </div>
    </div>
  );
}

function RetirementRoadmapStrip() {
  return (
    <div className="w-full sm:max-w-md border-b sm:border-2 border-white/50 bg-[linear-gradient(107.32deg,#00386E_38.01%,#002242_100%)] p-5 sm:p-6 text-white relative overflow-hidden rounded-t-6xl sm:rounded-4xl">
      <i className="absolute top-10 left-20 w-14 z-0 shadow-[-20px_-20px_120px_90px_#386DC4]" />

      <div className="text-2xl mb-3 sm:mb-4 relative z-2">
        Get Your Personalised Retirement Blueprint
      </div>
      <div className="opacity-65 text-2xs sm:text-sm leading-1.3! relative z-2">
        Free Retirement Blueprint PDF to understand how retirement planning
        works
      </div>

      <Image
        src="/next_assets/images/strips/holi-special-strip.png"
        alt="Retirement Plan Roadmap"
        width={300}
        height={170}
        className="absolute blur-[1px] opacity-20 sm:blur-none sm:opacity-100 max-w-45 -bottom-12 -right-7 object-contain shrink-0 z-1"
      />
      <Image
        src="/next_assets/images/overlap/sun-rays-circle.png"
        alt="Highlighted Icon Background Image"
        width={250}
        height={250}
        className="hidden sm:block absolute bottom-0 -right-14 opacity-45 translate-y-1/3 pointer-events-none z-0"
      />
    </div>
  );
}

function DefaultStrip() {
  return (
    <div className="w-full sm:max-w-md border-b sm:border-2 border-white/50 bg-[linear-gradient(107.32deg,#1A51AA_38.01%,#092758_97.14%)] p-5 sm:p-6 text-white relative overflow-hidden rounded-t-6xl sm:rounded-4xl">
      <i className="absolute top-10 left-20 w-14 z-0 shadow-[-20px_-20px_120px_90px_#c3daff94]" />
      <Icon.callIconV3 className="w-9 h-9 sm:w-10 sm:h-10 mb-6 sm:mb-9 relative z-1" />

      <div className="text-2xl mb-3 sm:mb-4">
        Get a Free Callback from Our Experts
      </div>
      <div className="opacity-65 text-2xs sm:text-sm sm:leading-5 leading-1.1">
        Our team will call you and guide you through every mutual fund query and
        concern.
      </div>
    </div>
  );
}
